import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Search, ArrowRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "./Seo";
import { Button } from "../ui/button";

const NAV = [
  { label: "Test Series", to: "/pricing", id: "explore-tests" },
  { label: "Exams", to: "/#exams", id: "exams" },
  { label: "Free Mocks", to: "/free-tests", id: "free-mocks" },
  { label: "Blog", to: "/blog", id: "blog" },
  { label: "About", to: "/about", id: "about" },
];

export default function Navbar() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => { const f = () => setScrolled(window.scrollY > 8); f(); window.addEventListener("scroll", f); return () => window.removeEventListener("scroll", f); }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const submitSearch = (e) => { e.preventDefault(); if (q.trim()) { nav(`/search?q=${encodeURIComponent(q)}`); } };

  return (
    <header className={`fixed top-0 z-50 w-full transition-all ${scrolled ? "glass-nav border-b border-line" : "bg-transparent"}`}>
      <div className="container-max container-px flex items-center justify-between h-16">
        <Link to="/" data-testid="nav-logo-brand" className="shrink-0"><Logo /></Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <Link key={n.id} to={n.to} data-testid={`nav-link-${n.id}`}
              className="text-[13px] font-medium text-content-secondary hover:text-content transition-colors px-3.5 py-2 rounded-lg hover:bg-white/5">{n.label}</Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2.5">
          <form onSubmit={submitSearch} className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="nav-search-input" placeholder="Search"
              className="bg-white/5 border border-line rounded-full pl-9 pr-4 py-2 text-[13px] w-32 focus:w-48 transition-all outline-none focus:border-white/20 placeholder:text-content-muted" />
          </form>
          {user ? (
            <Button asChild className="btn-ember rounded-full h-9 px-4 text-[13px]" data-testid="nav-dashboard-btn">
              <Link to={user.role === "student" ? "/student/dashboard" : "/admin"}>Dashboard</Link>
            </Button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="text-[13px] font-medium text-content-secondary hover:text-content px-3 py-2">Login</Link>
              <Button asChild className="btn-ember rounded-full h-9 px-4 text-[13px]" data-testid="nav-register-btn">
                <Link to="/register">Get Started <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
              </Button>
            </>
          )}
        </div>

        <button className="lg:hidden text-content p-1.5 -mr-1.5" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass-nav border-t border-line">
          <div className="container-px py-5">
            <form onSubmit={submitSearch} className="relative mb-4">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exams, tests…"
                className="w-full bg-white/5 border border-line rounded-xl pl-9 pr-4 py-3 text-sm outline-none" />
            </form>
            <div className="flex flex-col">
              {NAV.map((n) => (
                <Link key={n.id} to={n.to} className="py-3 text-[15px] text-content-secondary border-b border-line/60" data-testid={`nav-m-${n.id}`}>{n.label}</Link>
              ))}
            </div>
            <div className="flex gap-3 mt-5">
              {user ? (
                <Button asChild className="btn-ember rounded-full flex-1 h-11"><Link to={user.role === "student" ? "/student/dashboard" : "/admin"}>Dashboard</Link></Button>
              ) : (
                <>
                  <Button asChild variant="outline" className="flex-1 rounded-full border-line h-11 bg-transparent"><Link to="/login">Login</Link></Button>
                  <Button asChild className="btn-ember rounded-full flex-1 h-11"><Link to="/register">Get Started</Link></Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
